'use client'
// =============================================================================
// FilesTab — « Explorateur / Éditeur », DANS le panneau IA (pas un panneau de plus).
//
// Même canal que /bureau/files : la commande `shared_files` de l'agent de l'usager, confinée au dossier partagé qu'il a désigné.
// Règles : on n'affiche jamais un résultat non reçu ; rien n'est écrit sans le clic explicite « Enregistrer » ; « Espace utilisé »
// est la somme des tailles que l'agent a RAPPORTÉES pour les dossiers déjà ouverts — pas un quota (inconnu), donc pas de jauge.
// =============================================================================

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { pollCommandResult } from '@/components/agents/shared'

const API = process.env.NEXT_PUBLIC_API_URL ?? ''

interface Entry { name: string; type: 'file' | 'directory'; size?: number; path: string }
interface Agent { id: string; hostname: string; status: string }
type Sub = 'FICHIERS' | 'ÉDITEUR'

const join = (dir: string, name: string) => (dir ? `${dir}/${name}` : name)
const fmt = (n: number) => (n < 1024 ? `${n} o` : n < 1048576 ? `${(n / 1024).toFixed(1)} Ko` : n < 1073741824 ? `${(n / 1048576).toFixed(1)} Mo` : `${(n / 1073741824).toFixed(2)} Go`)
const ICON: Record<string, string> = { py: '🐍', ts: '📘', tsx: '⚛', js: '🟨', json: '📋', md: '📄', txt: '📝', pdf: '📕', png: '🖼', jpg: '🖼', zip: '📦' }
const icon = (e: Entry) => (e.type === 'directory' ? '📁' : ICON[e.name.split('.').pop()?.toLowerCase() ?? ''] ?? '📄')

export function FilesTab() {
  const [sub, setSub] = useState<Sub>('FICHIERS')
  const [agents, setAgents] = useState<Agent[] | null>(null)
  const [agentId, setAgentId] = useState('')
  const [tree, setTree] = useState<Record<string, Entry[]>>({})   // chemin du dossier → entrées déjà lues
  const [open, setOpen] = useState<Set<string>>(new Set(['']))
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [file, setFile] = useState<{ path: string; name: string; content: string; original: string; binary: boolean } | null>(null)
  const [saved, setSaved] = useState('')

  useEffect(() => {
    fetch(`${API}/api/agents`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { agents: [] }))
      .then((d: { agents?: Agent[] }) => {
        const online = (d.agents ?? []).filter(a => a.status === 'ONLINE')
        setAgents(online); if (online[0]) setAgentId(online[0].id)
      })
      .catch(() => setAgents([]))
  }, [])

  const run = useCallback(async (op: Record<string, unknown>, target: string): Promise<Record<string, unknown>> => {
    const res = await fetch(`${API}/api/agents/${agentId}/command`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ type: 'shared_files', payload: { ...op, path: target } }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const { commandId } = await res.json() as { commandId: string }
    const cmd = await pollCommandResult(agentId, commandId)
    if (cmd.status !== 'DONE') throw new Error(cmd.error || "L'agent n'a pas répondu")
    return (cmd.result ?? {}) as Record<string, unknown>
  }, [agentId])

  const list = useCallback(async (dir: string) => {
    if (!agentId) return
    setBusy(`lecture de ${dir || 'la racine'}`); setError('')
    try {
      const r = await run({ op: 'list' }, dir)
      const items = Array.isArray(r.items) ? r.items as { name: string; type?: string; size?: number; path?: string }[] : []
      const rows: Entry[] = items.map(it => ({ name: it.name, type: it.type === 'directory' ? 'directory' : 'file', size: it.size, path: typeof it.path === 'string' ? it.path : join(dir, it.name) }))
      rows.sort((a, b) => (a.type !== b.type ? (a.type === 'directory' ? -1 : 1) : a.name.localeCompare(b.name)))
      setTree(t => ({ ...t, [dir]: rows }))
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur') } finally { setBusy('') }
  }, [agentId, run])

  useEffect(() => { if (agentId) { setTree({}); setOpen(new Set([''])); void list('') } }, [agentId]) // eslint-disable-line react-hooks/exhaustive-deps

  const openFile = async (e: Entry) => {
    setBusy(`lecture de ${e.name}`); setError(''); setSaved('')
    try {
      const r = await run({ op: 'read' }, e.path)
      const content = typeof r.content === 'string' ? r.content : ''
      const garbled = content.length > 0 && (content.match(/�/g)?.length ?? 0) / content.length > 0.05
      const binary = r.binary === true || garbled
      setFile({ path: e.path, name: e.name, content: binary ? '' : content, original: binary ? '' : content, binary })
      setSub('ÉDITEUR')
    } catch (er) { setError(er instanceof Error ? er.message : 'Lecture impossible') } finally { setBusy('') }
  }

  const toggle = (e: Entry) => {
    if (e.type === 'file') return void openFile(e)
    setOpen(o => { const n = new Set(o); if (n.has(e.path)) n.delete(e.path); else n.add(e.path); return n })
    if (!tree[e.path]) void list(e.path)
  }

  const save = async () => {
    if (!file || file.binary) return
    setBusy(`enregistrement de ${file.name}`); setError(''); setSaved('')
    try { await run({ op: 'write', content: file.content }, file.path); setFile({ ...file, original: file.content }); setSaved('Enregistré sur la machine de l’agent.') }
    catch (er) { setError(er instanceof Error ? er.message : 'Enregistrement impossible') } finally { setBusy('') }
  }

  const used = Object.values(tree).flat().reduce((s, e) => s + (e.type === 'file' ? e.size ?? 0 : 0), 0)
  const dirty = !!file && file.content !== file.original

  const renderDir = (dir: string, depth: number): React.ReactNode =>
    (tree[dir] ?? []).map(e => (
      <div key={e.path}>
        <button onClick={() => toggle(e)} className="ft-row" style={{ paddingLeft: 8 + depth * 14 }}>
          <span style={{ width: 10, color: 'var(--text-muted)' }}>{e.type === 'directory' ? (open.has(e.path) ? '▾' : '▸') : ''}</span>
          <span>{icon(e)}</span><span className="ft-name">{e.name}</span>
          {e.type === 'file' && e.size !== undefined && <span className="ft-size">{fmt(e.size)}</span>}
        </button>
        {e.type === 'directory' && open.has(e.path) && (tree[e.path]
          ? (tree[e.path].length ? renderDir(e.path, depth + 1) : <div className="ft-empty" style={{ paddingLeft: 30 + depth * 14 }}>Dossier vide</div>)
          : <div className="ft-empty" style={{ paddingLeft: 30 + depth * 14 }}>Lecture…</div>)}
      </div>
    ))

  return (
    <div className="ft-root">
      <div className="ft-head">
        <div className="ft-title">Explorateur / Éditeur</div>
        <div className="ft-subtabs">
          {(['FICHIERS', 'ÉDITEUR'] as Sub[]).map(s => (
            <button key={s} onClick={() => setSub(s)} className={sub === s ? 'on' : ''}>{s === 'FICHIERS' ? 'Fichiers' : `Éditeur${dirty ? ' •' : ''}`}</button>
          ))}
        </div>
      </div>

      {agents === null && <div className="ft-note">Recherche de votre agent…</div>}
      {agents !== null && agents.length === 0 && (
        <div className="ft-note">Aucun agent en ligne : vos fichiers sont sur votre machine, l’explorateur a besoin de l’agent Amiral. <Link href="/bureau/fichiers">Ouvrir la page Fichiers →</Link></div>
      )}
      {error && <div className="ft-err">{error}</div>}
      {busy && <div className="ft-note">En cours : {busy}…</div>}

      {sub === 'FICHIERS' && agentId && (
        <div className="ft-tree">{renderDir('', 0)}{tree[''] && tree[''].length === 0 && <div className="ft-empty">Dossier partagé vide</div>}</div>
      )}

      {sub === 'ÉDITEUR' && (file ? (
        <div className="ft-editor">
          <div className="ft-fname">{file.path}</div>
          {file.binary
            ? <div className="ft-note">Fichier binaire : aucun aperçu texte possible.</div>
            : <textarea value={file.content} onChange={e => { setSaved(''); setFile({ ...file, content: e.target.value }) }} spellCheck={false} />}
          <div className="ft-actions">
            <button disabled={!dirty || file.binary || !!busy} onClick={save}>Enregistrer</button>
            <button disabled={!dirty} onClick={() => setFile({ ...file, content: file.original })}>Annuler les changements</button>
            {saved && <span className="ft-ok">{saved}</span>}
          </div>
        </div>
      ) : <div className="ft-note">Choisissez un fichier dans l’onglet Fichiers pour l’ouvrir ici. Rien n’est écrit sans votre clic sur « Enregistrer ».</div>)}

      <div className="ft-used">
        <div className="ft-used-top"><span>Espace utilisé</span><span>{fmt(used)}</span></div>
        <div className="ft-used-sub">Somme des fichiers des dossiers ouverts, telle que rapportée par l’agent (pas un quota).</div>
      </div>
    </div>
  )
}
